import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { setOrbitAISnackBar } from 'src/redux/siteAuditReducer';
import { getReviewAutomationSettingsQuery } from '../api/getReviewAutomationSettings';

interface UseGetReviewAutomationSettingsProps {
  accountId: string;
  locationId: string;
  siteId?: string;
  enabled?: boolean;
}

/**
 * Custom hook to fetch review automation settings for a specific accountId & locationId.
 */
export function useGetReviewAutomationSettings({
  accountId,
  locationId,
  siteId,
  enabled = true,
}: UseGetReviewAutomationSettingsProps) {
  const dispatch = useDispatch();
  const { _id: reduxSiteId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};
  const currentSiteId = siteId || reduxSiteId;

  const baseQuery = getReviewAutomationSettingsQuery(accountId, locationId, currentSiteId);
  const query = useQuery({
    ...baseQuery,
    enabled: !!accountId && !!locationId && enabled,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      const error: any = query.error;
      dispatch(
        setOrbitAISnackBar({
          status: error?.status || 'error',
          message: error?.message || 'Something went wrong. Please try again.',
        })
      );
    }
  }, [query.isError, query.error, dispatch]);

  return query;
}

export default useGetReviewAutomationSettings;
